<script>
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import aceleresIntegrations from 'dashboard/api/aceleres/integrations';
import {
  DISCONNECT_ALERT_MUTE_OPTIONS,
  DISCONNECT_ALERT_UNDO_HINT,
  dismissDisconnectedAlert,
  isDisconnectAlertMuted,
  disconnectedInstanceTypeLabel,
  findDisconnectedAccessibleInstances,
  isFunilChatFrame,
  reconnectionResponsibleLabel,
  wasDisconnectedAlertDismissed,
} from 'dashboard/helper/aceleresDisconnectedInstanceAlert';
import {
  clearIntegracoesPageData,
  getIntegracoesPageData,
  prefetchIntegracoesPageData,
} from 'dashboard/routes/dashboard/configuracoes/integracoesPageData';

export default {
  components: { ConfirmationModal },
  props: {
    accountId: {
      type: [Number, String],
      required: true,
    },
  },
  data: () => ({
    checkingAccountId: null,
    disconnectedInstances: [],
    alertTitle: 'Sua instância está desconectada',
    alertDescription:
      'Para utilizar todos os recursos do Zoraxy, reconecte sua conta.',
    reconnectLabel: 'Reconectar',
    dismissLabel: 'Agora não',
    typeLabelText: 'Tipo',
    reconnectResponsibleLabelText: 'Responsável pela reconexão',
    muteOptions: DISCONNECT_ALERT_MUTE_OPTIONS,
    undoHint: DISCONNECT_ALERT_UNDO_HINT,
    muteInstanceId: null,
    muteDuration: '7d',
    muting: false,
    muteError: '',
  }),
  watch: {
    accountId() {
      this.checkDisconnectedInstance();
    },
  },
  mounted() {
    this.checkDisconnectedInstance();
  },
  methods: {
    async checkDisconnectedInstance() {
      const accountId = String(this.accountId || '');
      if (!accountId || this.checkingAccountId === accountId) return;
      if (this.$route.name === 'configuracoes_integracoes') return;
      // Painel de conversa do Funil: o dashboard cabe num iframe estreito.
      // Mostrar o modal aqui tapa a conversa; não marcar como dispensado,
      // para o aviso continuar na aba completa do Zoraxy.
      if (isFunilChatFrame()) return;

      this.checkingAccountId = accountId;
      const payload =
        getIntegracoesPageData(accountId) ||
        (await prefetchIntegracoesPageData(accountId));
      const instances = findDisconnectedAccessibleInstances(
        payload?.whatsapp?.instances
      ).filter(
        instance =>
          !isDisconnectAlertMuted(instance) &&
          !wasDisconnectedAlertDismissed({
            storage: window.sessionStorage,
            accountId,
            instanceId: instance.id,
          })
      );

      if (!instances.length) {
        return;
      }

      this.disconnectedInstances = instances.map(instance => ({
        ...instance,
        typeLabel: disconnectedInstanceTypeLabel(instance.connection_type),
        reconnectResponsible: reconnectionResponsibleLabel(instance),
      }));
      this.updateTexts();

      instances.forEach(instance => {
        dismissDisconnectedAlert({
          storage: window.sessionStorage,
          accountId,
          instanceId: instance.id,
        });
      });

      const reconnectNow =
        await this.$refs.confirmationModal.showConfirmation();
      this.cancelMute();
      if (reconnectNow) {
        this.$router.push({
          name: 'configuracoes_integracoes',
          params: { accountId },
        });
      }
    },
    updateTexts() {
      const single = this.disconnectedInstances.length === 1;
      this.alertTitle = single
        ? 'Sua instância está desconectada'
        : 'Há instâncias desconectadas';
      this.alertDescription = single
        ? 'Para utilizar todos os recursos do Zoraxy, reconecte esta conta.'
        : 'Para utilizar todos os recursos do Zoraxy, reconecte as contas abaixo.';
    },
    openMute(instance) {
      this.muteInstanceId = instance.id;
      this.muteDuration = '7d';
      this.muteError = '';
    },
    cancelMute() {
      this.muteInstanceId = null;
      this.muteError = '';
    },
    async confirmMute(instance) {
      if (this.muting) return;
      this.muting = true;
      this.muteError = '';
      try {
        await aceleresIntegrations.updateWhatsappDisconnectAlert(
          instance.id,
          this.muteDuration
        );
      } catch (error) {
        this.muteError = error.message;
        return;
      } finally {
        this.muting = false;
      }

      // A página de Integrações precisa ler a marca nova (selo e "Reativar").
      clearIntegracoesPageData();
      this.muteInstanceId = null;
      this.disconnectedInstances = this.disconnectedInstances.filter(
        item => item.id !== instance.id
      );
      if (!this.disconnectedInstances.length) {
        this.$refs.confirmationModal.cancel();
        return;
      }
      this.updateTexts();
    },
  },
};
</script>

<template>
  <ConfirmationModal
    ref="confirmationModal"
    :title="alertTitle"
    :description="alertDescription"
    :confirm-label="reconnectLabel"
    :cancel-label="dismissLabel"
    mobile-emphasis
  >
    <template #body>
      <div
        class="mx-5 max-h-64 overflow-y-auto rounded-xl border border-n-weak bg-n-alpha-1"
        data-testid="disconnected-instance-list"
      >
        <article
          v-for="instance in disconnectedInstances"
          :key="instance.id"
          class="border-b border-n-weak px-4 py-3 last:border-b-0"
        >
          <div class="flex items-start justify-between gap-3">
            <p class="m-0 min-w-0 text-sm font-medium text-n-slate-12">
              {{
                instance.label || instance.name || `Instância ${instance.id}`
              }}
            </p>
            <button
              v-if="muteInstanceId !== instance.id"
              type="button"
              class="flex shrink-0 items-center gap-1 border-none bg-transparent p-0 text-xs text-n-slate-10 underline-offset-2 hover:text-n-slate-12 hover:underline"
              data-testid="disconnected-instance-mute"
              @click="openMute(instance)"
            >
              <i class="i-lucide-bell-off size-3.5" aria-hidden="true" />
              {{ 'Não mostrar mais' }}
            </button>
          </div>
          <p class="m-0 mt-1 text-xs text-n-slate-11">
            {{ `${typeLabelText}: ${instance.typeLabel}` }}
          </p>
          <p class="m-0 mt-1 text-xs text-n-slate-11">
            {{
              `${reconnectResponsibleLabelText}: ${instance.reconnectResponsible}`
            }}
          </p>
          <div
            v-if="muteInstanceId === instance.id"
            class="mt-3 rounded-lg border border-n-weak bg-n-solid-2 p-3"
            data-testid="disconnected-instance-mute-confirm"
          >
            <p class="m-0 text-xs text-n-slate-12">
              {{
                'Você não será mais avisado sobre a desconexão desta inbox. Deseja continuar?'
              }}
            </p>
            <label class="mt-2 block text-xs text-n-slate-11">
              {{ 'Não avisar' }}
              <select
                v-model="muteDuration"
                class="mb-0 mt-1 h-8 w-full rounded-md py-0 text-xs"
                data-testid="disconnected-instance-mute-duration"
              >
                <option
                  v-for="option in muteOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <p class="m-0 mt-2 text-xs text-n-slate-10">{{ undoHint }}</p>
            <p v-if="muteError" class="m-0 mt-2 text-xs text-n-ruby-11">
              {{ muteError }}
            </p>
            <div class="mt-3 flex justify-end gap-2">
              <button
                type="button"
                class="rounded-md border-none bg-n-alpha-2 px-3 py-1.5 text-xs text-n-slate-12"
                @click="cancelMute"
              >
                {{ 'Cancelar' }}
              </button>
              <button
                type="button"
                class="rounded-md border-none bg-n-brand px-3 py-1.5 text-xs text-white disabled:opacity-60"
                :disabled="muting"
                data-testid="disconnected-instance-mute-submit"
                @click="confirmMute(instance)"
              >
                {{ muting ? 'Salvando…' : 'Confirmar' }}
              </button>
            </div>
          </div>
        </article>
      </div>
    </template>
  </ConfirmationModal>
</template>
